import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg'
import api, { getResourceCategories } from '../../../utils/api'
import PostSummary from '../../../partials/Post/PostSummary'
import { Color } from '../../../utils/constants'
import Tag from '../../../components/Tag'
import { twMerge } from 'tailwind-merge'
import { FormattedMessage } from 'react-intl'
import { Link, useParams } from 'react-router-dom'

const MainGrid = () => {
    const { category } = useParams()

    const [posts, setPosts] = useState([])
    const [categories, setCategories] = useState<any[]>([])
    const [tags, setTags] = useState<any[]>([])

    const [activeCategory, setActiveCategory] = useState<number | null>(null)
    const [activeTags, setActiveTags] = useState<number[]>([])
    const [searchTerm, setSearchTerm] = useState<string | null>(null)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [previousPage, setPreviousPage] = useState<number>(currentPage)
    const [totalPages, setTotalPages] = useState<number | null>(null)

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        getResourceCategories(setCategories)
        api.get('/wp-json/wp/v2/tags').then((response) => setTags(response.data))
    }, [])

    useEffect(() => {
        if (category) {
            setActiveCategory(categories.find((cat) => cat.slug === category)?.id)
        } else {
            setActiveCategory(null)
        }
    }, [category, categories])

    const generateQuery = useCallback(() => {
        let query = '/wp-json/wp/v2/posts?_embed&per_page=9'
        if (activeCategory !== null) {
            query += `&categories=${activeCategory}`
        } else {
            const categoriesQuery = categories.map((category) => category.id).join(',')
            query += `&categories=${categoriesQuery}`
        }
        if (activeTags.length >= 1) {
            query += `&tags=${activeTags.join(',')}`
        }
        if (searchTerm !== null && searchTerm.length > 0) {
            query += `&search=${searchTerm}`
        }
        return query
    }, [activeCategory, activeTags, searchTerm, categories])

    const getPosts = (query: string) => {
        api.get(query).then((response) => {
            const pages: string | undefined = response.headers['x-wp-totalpages']
            setTotalPages(pages !== undefined ? parseInt(pages) : null)
            setPosts(response.data)
        })
    }

    useEffect(() => {
        setCurrentPage(1)
        getPosts(generateQuery())
    }, [generateQuery])

    useEffect(() => {
        if (previousPage !== currentPage) {
            getPosts(`${generateQuery()}&page=${currentPage}`)
        }
        setPreviousPage(currentPage)
    }, [currentPage, previousPage, generateQuery])

    const toggleActiveTag = (tagId: number) => {
        if (activeTags.includes(tagId)) {
            setActiveTags(activeTags.filter((id) => id !== tagId))
        } else {
            setActiveTags([...activeTags, tagId])
        }
    }

    const getCategoryButton = (value: number | null) => {
        const slug = getCategorySlug(value)
        return (
            <Link
                className='relative whitespace-nowrap'
                to={`/resources${value !== null ? '/' + slug : ''}`}
            >
                <button className={twMerge('pb-6 transition', activeCategory === value && 'text-purple')}>
                    <FormattedMessage tagName='h6' id={`category.${slug}`} />
                </button>
                {activeCategory === value && <div className={'absolute bg-purple h-px w-full'} />}
            </Link>
        )
    }

    const getCategorySlug = (value: number | null) => {
        if (!value) {
            return 'all'
        } else {
            return categories.find((cat) => cat.id === value)?.slug
        }
    }

    return (
        <div className='md:side-padding pt-16' ref={ref}>
            <div className='flex gap-x-6 overflow-x-scroll md:overflow-x-visible no-scrollbar side-padding md:p-0'>
                {getCategoryButton(null)}
                {categories.map(({ id }) => getCategoryButton(id))}
            </div>
            <div className='flex border-t border-black/10 flex-col md:flex-row'>
                <div className='md:w-[20%] md:pr-12 py-6 md:py-16 border-b md:border-b-0 md:border-r border-black/10 flex-shrink-0'>
                    <h6 className='pb-2 side-padding md:p-0'>Tags:</h6>
                    <div className='flex flex-wrap gap-2 side-padding md:p-0'>
                        {tags.map((tag) => (
                            <button onClick={() => toggleActiveTag(tag.id)} key={tag.slug}>
                                <Tag
                                    title={tag.name}
                                    color={activeTags.includes(tag.id) ? Color.BLACK : Color.WHITE}
                                />
                            </button>
                        ))}
                    </div>
                </div>
                <div className='side-padding md:pl-12 md:pr-0 pt-8 md:pt-16 w-full'>
                    <div className='flex items-center justify-between pb-8 md:pb-16'>
                        <FormattedMessage
                            id={`page.resources.${getCategorySlug(activeCategory)}`}
                            tagName='h4'
                        />
                        <div className='border border-black/10 rounded-full px-3 md:px-6 py-3 flex justify-between items-center'>
                            <input
                                onBlur={(event) => setSearchTerm(event.target.value)}
                                placeholder='search by keyword'
                                className='outline-0 placeholder:text-xs placeholder:uppercase h-0 w-0 md:w-full md:h-full'
                            />
                            <button>
                                <SearchIcon className='w-4 h-4' />
                            </button>
                        </div>
                    </div>
                    {posts.length > 0 ? (
                        <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-10 pb-16'>
                            {posts.map((post) => (
                                <PostSummary
                                    post={post}
                                    className='w-full md:w-full'
                                    color={Color.BLACK}
                                    // @ts-ignore
                                    key={`post-${post.id}`}
                                />
                            ))}
                        </div>
                    ) : (
                        <div>
                            <p>There are no posts found that match your search parameters.</p>
                        </div>
                    )}
                    {totalPages !== null && totalPages > 1 && (
                        <div className='flex items-center w-max mx-auto pb-16'>
                            {/*@ts-ignore*/}
                            {[...Array(totalPages).keys()].map((page) => (
                                <button
                                    key={`pagination-${page}`}
                                    onClick={() => {
                                        setCurrentPage(page + 1)
                    ref.current?.scrollIntoView()
                                    }}
                                    className={twMerge(
                                        'rounded-full flex items-center justify-center w-10 h-10 transition',
                                        page + 1 === currentPage && 'bg-black/10',
                                    )}
                                >
                                    {page + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default MainGrid
